import { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MetaTags from '../components/MetaTags';
import StripePaymentStep from '../components/StripePaymentStep';
import api from '../api/axios';
import './new-pages.css';
import './MembershipLookup.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TYPE_LABELS = {
  membership_family:  { label: 'Family Membership',     period: '2 Years', amount: 5000, display: '$50.00 AUD' },
  membership_single:  { label: 'Individual Membership', period: '1 Year',  amount: 2500, display: '$25.00 AUD' },
};

const RED  = '#C41E3A';
const BLUE = '#1B3A6B';

const fmt = iso => new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ── Canvas card download ──────────────────────────────────────────────────
async function downloadCard(member, logoSrc) {
  const W = 680, H = 400;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, BLUE);
  bg.addColorStop(1, '#0d2248');
  ctx.fillStyle = bg;
  roundRect(ctx, 0, 0, W, H, 20);
  ctx.fill();

  // Red top stripe
  ctx.fillStyle = RED;
  ctx.fillRect(0, 0, W, 7);

  // Logo (top-right)
  if (logoSrc) {
    try {
      const logo = await loadImage(logoSrc);
      const lSize = 54;
      const lX = W - lSize - 32, lY = 24;
      ctx.save();
      roundRect(ctx, lX - 3, lY - 3, lSize + 6, lSize + 6, 10);
      ctx.clip();
      ctx.drawImage(logo, lX, lY, lSize, lSize);
      ctx.restore();
    } catch {}
  }

  // Org name + brand
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '500 13px system-ui, sans-serif';
  ctx.fillText('Melbourne Nepalese Community West Inc.', 40, 48);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 32px system-ui, sans-serif';
  ctx.fillText('MNCW', 40, 88);

  // Red divider
  ctx.fillStyle = 'rgba(196, 30, 58, 0.5)';
  ctx.fillRect(40, 104, W - 80, 1);

  const typeInfo = TYPE_LABELS[member.membership_type] || TYPE_LABELS.membership_family;
  const isActive = member.expires_at ? new Date(member.expires_at) > new Date() : false;

  // Member name
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 26px system-ui, sans-serif';
  ctx.fillText(`${member.first_name} ${member.last_name}`, 40, 148);
  let typeY = 173;
  if (member.spouse_name) {
    ctx.fillStyle = 'rgba(196, 30, 58, 0.9)';
    ctx.font = '13px system-ui, sans-serif';
    ctx.fillText(`♥  ${member.spouse_name}`, 40, 169);
    typeY = 190;
  }
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.font = '15px system-ui, sans-serif';
  ctx.fillText(`${typeInfo.label} · ${typeInfo.period}`, 40, typeY);

  // Membership number block
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  roundRect(ctx, 40, 210, 220, 60, 10);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font = '10px system-ui, sans-serif';
  ctx.fillText('MEMBERSHIP NUMBER', 56, 230);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 22px monospace';
  ctx.fillText(member.membership_no, 56, 258);

  // Status badge
  ctx.fillStyle = isActive ? '#1a7a3a' : '#b91c1c';
  roundRect(ctx, 290, 218, isActive ? 90 : 95, 30, 15);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 12px system-ui, sans-serif';
  ctx.fillText(isActive ? '● Active' : '● Expired', 306, 238);

  // Dates
  const dateY = 302;
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '12px system-ui, sans-serif';
  if (member.paid_at)    ctx.fillText(`Active since:  ${fmt(member.paid_at)}`, 40, dateY);
  if (member.expires_at) ctx.fillText(`Expires:           ${fmt(member.expires_at)}`, 40, dateY + 22);

  // Footer
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.fillRect(40, H - 52, W - 80, 1);
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.font = '10px system-ui, sans-serif';
  ctx.fillText('mncwestv@gmail.com  ·  ABN 19 828 036 229  ·  Reg. A0105154V', 40, H - 26);

  const link = document.createElement('a');
  link.download = `MNCW-${member.membership_no}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function MembershipLookupPage() {
  const [site, setSite]               = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [form, setForm]               = useState({ first_name: '', last_name: '', email: '' });
  const [member, setMember]           = useState(null);
  const [error, setError]             = useState('');
  const [searching, setSearching]     = useState(false);
  const [walletMsg, setWalletMsg]     = useState('');
  const [walletLoading, setWalletLoading] = useState('');
  const isApple = useRef(/iPad|iPhone|iPod|Macintosh/i.test(navigator.userAgent)).current;

  // Payment state
  const [clientSecret, setClientSecret]   = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [payStep, setPayStep]             = useState(false);
  const [payConfirming, setPayConfirming] = useState(false);
  const [renewed, setRenewed]             = useState(false);

  const [partners, setPartners] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/content/site'),
      api.get('/partners').catch(() => ({ data: [] })),
    ]).then(([siteRes, partnersRes]) => {
      setSite(siteRes.data);
      setPartners(partnersRes.data || []);
    }).finally(() => setPageLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSearch = async (e) => {
    e.preventDefault();
    setError(''); setMember(null); setPayStep(false); setClientSecret(''); setRenewed(false); setWalletMsg('');
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setError('Please enter your first name, last name, and email address.');
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get('/members/search', { params: form });
      setMember(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No membership found for these details. Please check and try again.');
    } finally { setSearching(false); }
  };

  const reset = () => {
    setForm({ first_name: '', last_name: '', email: '' });
    setMember(null); setError(''); setWalletMsg('');
    setPayStep(false); setClientSecret(''); setRenewed(false);
  };

  const startPayment = async () => {
    setError('');
    try {
      const typeMap = { family: 'membership_family', individual: 'membership_single' };
      const memberType = typeMap[member.membership_type] || member.membership_type || 'membership_family';
      const { data } = await api.post('/payments/intent', {
        type: memberType,
        recordId: member.id,
      });
      setClientSecret(data.clientSecret);
      setPaymentAmount(data.amount);
      setPayStep(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not initiate payment. Please try again.');
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    setPayConfirming(true);
    try {
      const { data } = await api.post('/payments/confirm', { paymentIntentId });
      setMember(data);
      setRenewed(true);
    } catch {
      setRenewed(true);
    } finally {
      setPayStep(false);
      setClientSecret('');
      setPayConfirming(false);
    }
  };

  const addToAppleWallet = async () => {
    if (!member) return;
    setWalletLoading('apple'); setWalletMsg('');
    try {
      await api.get('/wallet/apple', { params: { membership_no: member.membership_no } });
    } catch (err) {
      setWalletMsg(err.response?.data?.unsupported
        ? 'Apple Wallet is not configured for this site. Please use Download Card instead.'
        : (err.response?.data?.error || 'Apple Wallet not available right now.'));
    } finally { setWalletLoading(''); }
  };

  if (pageLoading) return <div className="page-loading"><div className="spinner-ring"></div></div>;
  if (!site) return null;

  const typeInfo  = member ? (TYPE_LABELS[member.membership_type] || TYPE_LABELS.membership_family) : null;
  const isPaid    = member?.payment_status === 'paid';
  const notExpired = member?.expires_at ? new Date(member.expires_at) > new Date() : false;
  const isActive  = isPaid && notExpired;

  return (
    <div className="App">
      <MetaTags
        title="Check Membership | Melbourne Nepalese Community West"
        description="Look up your MNCW membership number and status by entering your name and email address."
        url="https://mncw.org.au/membership-lookup"
        image="https://mncw.org.au/assets/img/logo.jpg"
        type="website"
      />
      <Header data={site} />

      <div className="community-page-container bg-white">
        <div className="container">
          <div className="page-header">
            <span className="page-subtitle">Already a Member?</span>
            <h1>Check My Membership</h1>
            <p className="page-intro">Enter your details to view your membership status, number, and expiry.</p>
          </div>

          <div className="membership-check-wrapper" style={{ marginBottom: 0 }}>

            {/* ── Search form ── */}
            {!member && (
              <div className="lookup-card">
                <form onSubmit={handleSearch} noValidate>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div className="mncw-form-group" style={{ marginBottom: 0 }}>
                      <label className="mncw-label">First Name</label>
                      <input className="mncw-input" placeholder="e.g. Ram"
                        value={form.first_name} onChange={e => set('first_name', e.target.value)} />
                    </div>
                    <div className="mncw-form-group" style={{ marginBottom: 0 }}>
                      <label className="mncw-label">Last Name</label>
                      <input className="mncw-input" placeholder="e.g. Sharma"
                        value={form.last_name} onChange={e => set('last_name', e.target.value)} />
                    </div>
                  </div>
                  <div className="mncw-form-group">
                    <label className="mncw-label">Email Address</label>
                    <input type="email" className="mncw-input" placeholder="your@email.com"
                      value={form.email} onChange={e => set('email', e.target.value)} />
                  </div>

                  {error && (
                    <div className="lookup-error">
                      <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                  )}

                  <button type="submit" className="btn-mncw" style={{ width: '100%', justifyContent: 'center' }} disabled={searching}>
                    {searching
                      ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Searching…</>
                      : <><i className="fas fa-search" style={{ marginRight: 8 }}></i>Check Membership</>}
                  </button>
                </form>
              </div>
            )}

            {/* ── Result ── */}
            {member && (
              <div className="membership-result-wrapper">

                {/* Renewal success banner */}
                {renewed && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '14px 18px', marginBottom: 16, color: '#15803d', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <i className="fas fa-check-circle fa-lg"></i>
                    <div>
                      <div style={{ fontWeight: 700 }}>Payment successful — membership renewed!</div>
                      {member.expires_at && <div style={{ fontSize: '0.85rem', marginTop: 2 }}>New expiry: <strong>{fmt(member.expires_at)}</strong></div>}
                    </div>
                  </div>
                )}

                {/* Visual card */}
                <div className="membership-card-visual">
                  <div className="mcv-header">
                    {site?.logo?.src && (
                      <img src={site.logo.src} alt="MNCW" className="mcv-logo" />
                    )}
                    <div className="mcv-header-text">
                      <span className="mcv-org">Melbourne Nepalese Community West</span>
                      <span className="mcv-brand">MNCW</span>
                    </div>
                  </div>
                  <div className="mcv-divider"></div>
                  <div className="mcv-name">{member.first_name} {member.last_name}</div>
                  {member.spouse_name && (
                    <div className="mcv-spouse">
                      <i className="fas fa-heart"></i> {member.spouse_name}
                    </div>
                  )}
                  <div className="mcv-type">{typeInfo.label} · {typeInfo.period}</div>
                  <div className="mcv-row">
                    <div className="mcv-number-block">
                      <div className="mcv-number-label">Membership No.</div>
                      <div className="mcv-number">{member.membership_no}</div>
                    </div>
                    <div className={`mcv-status ${isActive ? 'active' : 'expired'}`}>
                      <i className={`fas ${isActive ? 'fa-check-circle' : member.payment_status === 'pending' ? 'fa-clock' : 'fa-times-circle'}`}></i>
                      {isActive ? 'Active' : member.payment_status === 'pending' ? 'Pending' : 'Expired'}
                    </div>
                  </div>
                  <div className="mcv-dates">
                    {member.paid_at    && <span><i className="fas fa-calendar-check"></i> Since {fmt(member.paid_at)}</span>}
                    {member.expires_at && <span><i className="fas fa-clock"></i> Expires {fmt(member.expires_at)}</span>}
                  </div>
                  <div className="mcv-footer">mncwestv@gmail.com · ABN 19 828 036 229</div>
                </div>

                {/* ── Active: wallet / download ── */}
                {isActive && !payStep && (
                  <div className="wallet-actions">
                    <p className="wallet-actions-title">Save your card</p>

                    {isApple && (
                      <button className="wallet-btn apple-wallet-btn" onClick={addToAppleWallet} disabled={!!walletLoading}>
                        {walletLoading === 'apple'
                          ? <><i className="fas fa-spinner fa-spin"></i> Adding…</>
                          : <><i className="fab fa-apple" style={{ marginRight: 8, fontSize: '1.1rem' }}></i>Add to Apple Wallet</>}
                      </button>
                    )}

                    <button className="wallet-btn download-btn" onClick={() => downloadCard(member, site?.logo?.src)}>
                      <i className="fas fa-download" style={{ marginRight: 8 }}></i>Download Card
                    </button>

                    {walletMsg && (
                      <div className="wallet-msg">
                        <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>{walletMsg}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Expired / Pending: payment panel ── */}
                {!isActive && !payStep && (
                  <div className="wallet-actions">
                    <p className="wallet-actions-title">
                      {member.payment_status === 'pending' ? 'Complete Your Payment' : 'Renew Your Membership'}
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', marginBottom: 4 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: BLUE }}>{typeInfo.label}</div>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{typeInfo.period} validity</div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '1.1rem', color: BLUE }}>{typeInfo.display}</div>
                    </div>

                    {error && (
                      <div className="wallet-msg" style={{ background: '#fff3f3', border: '1px solid #ffcccc', color: RED, marginBottom: 4 }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>{error}
                      </div>
                    )}

                    <button className="wallet-btn" onClick={startPayment}
                      style={{ background: BLUE, color: '#fff' }}>
                      <i className="fas fa-credit-card" style={{ marginRight: 8 }}></i>
                      {member.payment_status === 'pending' ? 'Pay Now' : 'Renew Membership'}
                    </button>
                  </div>
                )}

                {/* ── Stripe payment step ── */}
                {payStep && clientSecret && (
                  <div className="wallet-actions">
                    <p className="wallet-actions-title">Secure Payment</p>
                    <Elements stripe={stripePromise} options={{
                      clientSecret,
                      appearance: {
                        theme: 'stripe',
                        rules: {
                          '.p-LinkOptIn': { display: 'none' },
                          '.p-LinkDefaultOptIn': { display: 'none' },
                        },
                      },
                    }}>
                      <StripePaymentStep
                        amount={paymentAmount}
                        onSuccess={handlePaymentSuccess}
                        onBack={() => { setPayStep(false); setClientSecret(''); }}
                        loading={payConfirming}
                      />
                    </Elements>
                  </div>
                )}

                <button className="btn-mncw btn-mncw-outline" onClick={reset}
                  style={{ marginTop: 20, width: '100%', justifyContent: 'center' }}>
                  <i className="fas fa-search" style={{ marginRight: 8 }}></i>Search Again
                </button>
              </div>
            )}
          </div>

          {/* ── Business Partners ── */}
          {partners.length > 0 && (
            <div className="partners-section">
              <div className="partners-section-header">
                <h2><i className="fas fa-handshake" style={{ color: RED, marginRight: 10 }}></i>Member Benefits</h2>
                <p>As an MNCW member, you enjoy exclusive discounts and offers from our trusted business partners.</p>
              </div>
              <div className="partners-grid">
                {partners.map(p => (
                  <div key={p.id} className="partner-card">
                    <div className="partner-card-header">
                      {p.logo
                        ? <img src={p.logo} alt={p.name} className="partner-logo" />
                        : <div className="partner-logo-placeholder"><i className="fas fa-building"></i></div>}
                      <div>
                        <div className="partner-name">{p.name}</div>
                        <div className="partner-category">{p.category}</div>
                      </div>
                    </div>
                    {p.discount && (
                      <div className="partner-discount">
                        <i className="fas fa-tag"></i>{p.discount}
                      </div>
                    )}
                    {p.description && (
                      <div className="partner-description">{p.description}</div>
                    )}
                    <div className="partner-info">
                      {(p.address || p.suburb) && (
                        <div className="partner-info-row">
                          <i className="fas fa-map-marker-alt"></i>
                          <span>{[p.address, p.suburb, p.state, p.postcode].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      {p.phone && (
                        <div className="partner-info-row">
                          <i className="fas fa-phone"></i>
                          <a href={`tel:${p.phone}`}>{p.phone}</a>
                        </div>
                      )}
                      {p.email && (
                        <div className="partner-info-row">
                          <i className="fas fa-envelope"></i>
                          <a href={`mailto:${p.email}`}>{p.email}</a>
                        </div>
                      )}
                      {p.website && (
                        <div className="partner-info-row">
                          <i className="fas fa-globe"></i>
                          <a href={p.website} target="_blank" rel="noopener noreferrer">
                            {p.website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer data={site} />
    </div>
  );
}
